import React, { useMemo } from 'react';
import { CrudExtended, IconType } from '@/Components/Controls/CrudExtended/CrudExtended';
import { NumberFormInput } from '@/Components/Controls/Input/NumberFormInput';
import { FormSelectInput } from '@/Components/Controls/SelectInput/FormSelectInput';
import { FormData } from '@/ModalWindows/OrderAutomatizationModal/hooks/useOrderAutomatizationModalForm';
import { OrderAutomatizationPackageModel } from '@/Models/OrderUserProducts/OrderAutomatizationPackageModel';
import { usePackageRowForm } from './hooks/usePackageRowForm';
import { useLocalization } from '@/Hooks';
import styles from './PackageSelect.scss';
import { BUTTON_THEMES } from '@/Components/Controls/Button/ButtonThemes';
import { Button } from '@/Components/Controls/Button/Button';
import { EMPTY_ACTION } from '@/Constants/SharedConstants';

type Props = {
  index: number;
  rowIndex: number;
  disabled?: boolean;
  isAddActive: boolean;
  onAdd: () => void;
  onDelete: () => void;
  packages: OrderAutomatizationPackageModel[];
  isOwnContract?: boolean;
  orderId?: number;
};

export const PackageSelect = ({
  index,
  rowIndex,
  isAddActive,
  onAdd,
  onDelete,
  packages,
  disabled = false,
  isOwnContract = true,
  orderId,
}: Props) => {
  const {
    modalWindows: {
      orderLinkWithProduct: {
        Placeholders,
        ShippingContract: { Packages, Weight },
      },
    },
    buttons: { Upload },
  } = useLocalization();

  const {
    rules: { required },
    options: { isPackageChanged },
    methods: { register, setValue, errors, loadProducts },
  } = usePackageRowForm(rowIndex, index, packages, orderId);

  const packageOptions = useMemo(() => {
    return packages.map((p) => ({
      key: p.key,
      value: p.description,
    }));
  }, [packages]);

  return (
    <div className={styles.packageSelect}>
      {index == 0 && (
        <>
          <div />
          <div className={styles.label}>{Packages}</div>
          <div className={styles.label}>{Weight}</div>
          <div />
          <div />
        </>
      )}

      {index === 0 ? (
        <CrudExtended
          className={styles.icons}
          iconItems={[
            {
              icon: IconType.Create,
              active: isAddActive && !disabled,
              onClick: onAdd,
            },
          ]}
        />
      ) : (
        <CrudExtended
          className={styles.icons}
          iconItems={[
            {
              icon: IconType.Delete,
              active: !disabled,
              onClick: onDelete,
            },
          ]}
        />
      )}

      <FormSelectInput<FormData>
        name={`prepareLabels.${rowIndex}.packages.${index}.key`}
        register={register}
        rules={required}
        className={styles.select}
        needClearButton={false}
        error={errors}
        options={[{ key: '', value: Placeholders.SelectPackage }, ...packageOptions]}
        disabled={disabled}
      />

      <NumberFormInput
        name={`prepareLabels.${rowIndex}.packages.${index}.weight`}
        register={register}
        setValue={setValue}
        rules={required}
        type={'weight'}
        placeholder={Placeholders.Weight}
        error={errors}
        disabled
      />

      <CrudExtended
        className={styles.icons}
        iconItems={[
          {
            icon: IconType.Edit,
            active: false,
            onClick: EMPTY_ACTION,
          },
        ]}
      />

      {!isOwnContract && isPackageChanged && (
        <Button text={Upload} theme={BUTTON_THEMES.PRIMARY} onClick={loadProducts} />
      )}
    </div>
  );
};
